import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Progress } from "../ui/progress";
import { FolderKanban, Calendar, ArrowUpRight } from "lucide-react";
import { Project, User } from "../../types";

interface ProjectOverviewProps {
  projects: Project[];
  currentUser: User;
}

export function ProjectOverview({
  projects,
  currentUser,
}: ProjectOverviewProps) {
  const getFilteredProjects = () => {
    if (currentUser.role === "admin") {
      return projects;
    }
    if (currentUser.role === "supervisor") {
      return projects.filter((project) => project.supervisorId === currentUser.id);
    }
    return projects.filter((project) => project.fabricatorIds.includes(currentUser.id));
  };

  const filteredProjects = getFilteredProjects();

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20";
      case "in-progress":
        return "bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/20";
      case "review":
        return "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20";
      case "on-hold":
        return "bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20";
      case "planning":
      default:
        return "bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20";
    }
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case "urgent":
      case "high":
        return "bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20";
      case "medium":
        return "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20";
      case "low":
      default:
        return "bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20";
    }
  };

  return (
    <Card className="overflow-hidden rounded-2xl border border-slate-200 dark:border-[#232730] bg-white dark:bg-[#121418] shadow-sm dark:shadow-xl transition-all">
      <CardHeader className="border-b border-slate-100 dark:border-[#232730] p-4 sm:p-6 pb-3.5 sm:pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9.5 w-9.5 sm:h-10 sm:w-10 items-center justify-center rounded-lg border border-slate-200 dark:border-[#232730] bg-slate-50 dark:bg-[#0E1014] text-slate-900 dark:text-white shadow-xs shrink-0">
              <FolderKanban className="h-4.5 w-4.5 sm:h-5 sm:w-5 text-slate-700 dark:text-zinc-200" />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-base sm:text-lg font-bold tracking-tight text-slate-900 dark:text-white font-display">
                Project Overview
              </CardTitle>
              <p className="text-xs text-slate-500 dark:text-zinc-400 font-medium truncate">
                Track live progress, priority, and milestone deliveries
              </p>
            </div>
          </div>
          <button
            onClick={() => (window.location.hash = "projects")}
            className="text-xs font-semibold text-slate-600 dark:text-zinc-300 hover:text-slate-950 dark:hover:text-white flex items-center gap-1 transition-colors cursor-pointer shrink-0 ml-2"
          >
            View All
            <ArrowUpRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </CardHeader>

      <CardContent className="space-y-3 p-4 sm:p-6">
        {filteredProjects.slice(0, 5).map((project) => (
          <div
            key={project.id}
            className="rounded-xl border border-slate-200/80 dark:border-[#232730] bg-slate-50/50 dark:bg-[#0E1014]/60 p-3.5 sm:p-5 hover:border-slate-300 dark:hover:border-[#323846] transition-all"
          >
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <h4 className="truncate text-sm sm:text-base font-bold text-slate-900 dark:text-white font-sans">
                    {project.name}
                  </h4>
                  <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-500 dark:text-zinc-400">
                    <Calendar className="h-3.5 w-3.5" />
                    <span>Due {new Date(project.endDate).toLocaleDateString()}</span>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-1.5">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-md border text-[11px] font-semibold uppercase tracking-wider ${getStatusBadge(
                      project.status
                    )}`}
                  >
                    {project.status.replace(/-/g, " ")}
                  </span>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-md border text-[11px] font-semibold uppercase tracking-wider ${getPriorityBadge(
                      project.priority
                    )}`}
                  >
                    {project.priority}
                  </span>
                </div>
              </div>

              <div className="rounded-lg border border-slate-200/70 dark:border-[#232730] bg-white dark:bg-[#121418] p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-zinc-400 font-mono">
                    Progress
                  </span>
                  <span className="text-xs font-bold text-slate-900 dark:text-white font-mono">
                    {project.progress}%
                  </span>
                </div>
                <Progress value={project.progress} className="h-2 bg-slate-100 dark:bg-[#1A1D24]" />
              </div>
            </div>
          </div>
        ))}

        {filteredProjects.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-200 dark:border-[#232730] bg-slate-50/50 dark:bg-[#0E1014]/40 px-4 py-10 text-center text-sm text-slate-500 dark:text-zinc-400">
            No projects assigned to you
          </div>
        )}
      </CardContent>
    </Card>
  );
}
